import { useRecoilValue, useSetRecoilState } from "recoil";
import { cbContactPersonToDeleteArrayState, cbErrorSectionValidationState, isGlobalEditModeFormDirtyState, isGlobalEditModeState, queryFieldsState } from "../state/customerbaseState";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { useTranslation } from "react-i18next";
import { CBAuth, CBCustomerState, CBCustomerStateId, CBExtraAddressAction, CBExtraAddressDeltaFields, CBPartnerType, IFormValues } from "../types";
import { useForm, useFormContext, useFormState, useWatch } from "react-hook-form";
import { customerFormToEditCustomerParams, shouldAskEmailScope } from "../utils";
import CustomerBaseModal from "./modal/CustomerBaseModal";
import { useContext, useEffect, useMemo, useState } from "react";
import { useHasScope } from "../../../config/globalConfig";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Box from "@mui/material/Box";
import { FormAutocomplete } from "../../booking/components/fields";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getStatusReasons, updateCustomerStatus } from "../api/service/customerbaseApi";
import { GlobalContext } from "../../../util/state/GlobalContextProvider";
import { setSnackbar } from "../../../util/state/globalAction";
import { snackbarTypes } from "../../../util/state/globalVariables";
import * as yup from "yup"
import { yupResolver } from "@hookform/resolvers/yup";
import DialogContentText from "@mui/material/DialogContentText";
import { WarningAmberRounded } from "@mui/icons-material";
import dayjs from "dayjs";

function toTitleCase(str: string) {
    return str.replace(/\w\S*/g, function(txt){
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
}

type TChangeStatusForm = {
    status: string | null;
    reason: string | null;
};

const CBMultiSectionActionButtons = () => {

    const hasScope = useHasScope;
    const { t } = useTranslation("customerbase");
    const { dispatchGlobalData } = useContext(GlobalContext);
    const { isDirty, defaultValues } = useFormState<IFormValues>();
    /**
     * CR-DRIVALIA-EPCH (LPP-22837): valori in attesa della scelta dell'operatore sullo scope del
     * cambio e-mail (solo contatti / anche fatturazione). Non nullo = modale aperta.
     */
    const [pendingEmailScopeValues, setPendingEmailScopeValues] = useState<any | null>(null);
    const { getValues, reset, trigger } = useFormContext<IFormValues>();
    const { globalData } = useContext(GlobalContext);
    const isGlobalEditMode = useRecoilValue(isGlobalEditModeState);
    const setIsGlobalEditMode = useSetRecoilState(isGlobalEditModeState);

    const setIsGlobalEditModeFormDirty = useSetRecoilState(isGlobalEditModeFormDirtyState);

    const setErrorSection = useSetRecoilState(cbErrorSectionValidationState);

    const cbContactPersonToDeleteArray = useRecoilValue(cbContactPersonToDeleteArrayState);
    const setCbContactPersonToDeleteArray = useSetRecoilState(cbContactPersonToDeleteArrayState);

    const setQueryFields = useSetRecoilState(queryFieldsState);

    const [showConfirmChangeStatus, setShowConfirmChangeStatus] = useState(false);

    const customerStateWatch = useWatch<IFormValues, 'cbDetails.customerState'>({name: "cbDetails.customerState"});
    const customerIdWatch = useWatch<IFormValues, 'cbDetails.cbCustomerId'>({ name: "cbDetails.cbCustomerId" });
    const customerBpCodeWatch = useWatch<IFormValues, 'cbDetails.bpCode'>({ name: "cbDetails.bpCode" });
    const [showChangeStatusDialog, setShowChangeStatusDialog] = useState(false);

    const updateStatusMutation = useMutation(updateCustomerStatus, {
        onError: (error) => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('error.tryLater'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    },
                },
            });
            handleCloseChangeStatusDialog();
        },
        onSuccess: () => {
            window.location.reload();
        }
    });

    const changeStatusForm = useForm<TChangeStatusForm>({
        defaultValues: {
            status: null,
            reason: null,
        },
        resolver: yupResolver(yup.object({
            status: yup.string().nullable().required("message.validation.required"),
            reason: yup.string().nullable()
                .test({
                    message: "message.validation.required",
                    test(value, context) {
                        console.log({ value, context }, context.parent.status)
                        return Boolean(value || context.parent.status === "ACTIVE");
                    }
                }),
        }), {}, { rawValues: true }),
    });
    const statusWatch = useWatch({
        control: changeStatusForm.control,
        name: "status",
    });

    const handleCloseChangeStatusDialog = () => {
        changeStatusForm.reset();
        setShowChangeStatusDialog(false);
    }

    const handleSubmitChangesStatusDialog = () => {
        const stateId = (CBCustomerStateId as any)[changeStatusForm.getValues('status')!];
        updateStatusMutation.mutate({
            bpCode: customerBpCodeWatch,
            cbCustomerId: customerIdWatch,
            customerState: changeStatusForm.getValues('status')!,
            reason: changeStatusForm.getValues('reason'),
            assistantId: globalData?.user.username,
            dateTime: dayjs()
        })
    }

    const changeStatusReasonQuery = useQuery(["getStatusReasons"], {
        enabled: true,
        queryFn: getStatusReasons,
    });


    useEffect(() => {
        if(!isGlobalEditMode) setIsGlobalEditModeFormDirty(false);
        else if(isDirty) setIsGlobalEditModeFormDirty(true);

    }, [isGlobalEditMode, isDirty]);

    /**
     * CR-DRIVALIA-EPCH (LPP-22837): `applyEmailToInvoicing` porta la scelta dell'operatore fino
     * alla composizione del payload (vedi `customerFormToEditCustomerParams`).
     */
    const onSubmit = (params: any, applyEmailToInvoicing: boolean = false) => {

        var extraAddressesDelta: CBExtraAddressDeltaFields[] = [];
        const cbCustomerId = getValues("cbDetails.cbCustomerId");

        const defaultExtraAddresses = defaultValues?.cbExtraAddresses || [];
        const currentExtraAddresses = getValues("cbExtraAddresses");

        defaultExtraAddresses.forEach(def => {
            if(!!def && (currentExtraAddresses.findIndex(curr => !!curr.idAssociation && (curr.idAssociation == def.idAssociation)) == -1)) {
                extraAddressesDelta.push({
                    action: CBExtraAddressAction.UNLINK,
                    link: {
                        customerId: cbCustomerId,
                        customerIdToLink: def.customerId || null,
                    }
                });
            }
        });

        currentExtraAddresses.filter(curr => !curr.idAssociation).forEach(curr => {
            extraAddressesDelta.push({
                action: CBExtraAddressAction.LINK,
                link: {
                    customerId: cbCustomerId,
                    customerIdToLink: curr.customerId || null,
                }
            });
        });

        setQueryFields(customerFormToEditCustomerParams(params, cbContactPersonToDeleteArray, extraAddressesDelta, applyEmailToInvoicing));
    }

    const onReset = () => {

        if(isDirty) reset();

        setErrorSection({
            cbDetails: false,
            cbPersonalOrganizationInfo: false,
            cbInvoicingAddress: false,
            salesAreas: false,
            cbPaymentInfo: false,
            cbCorporateInfo: false
        });

        if(cbContactPersonToDeleteArray.length > 0)
            setCbContactPersonToDeleteArray([]);

        setIsGlobalEditMode(false);

    }

    const handleSubmit = () => {
        trigger().then((isValid) => {
            if(!isValid) {
                trigger("cbDetails").then((isValid) => {
                    setErrorSection((prev) => {
                        return {...prev, ["cbDetails"]: !isValid}
                    });
                });
                trigger("cbPersonalInfo").then((isValid) => {
                    setErrorSection((prev) => {
                        return {...prev, ["cbPersonalOrganizationInfo"]: !isValid}
                    });
                });
                trigger("cbInvoicingAddress").then((isValid) => {
                    setErrorSection((prev) => {
                        return {...prev, ["cbInvoicingAddress"]: !isValid}
                    });
                });
                trigger("salesAreas").then((isValid) => {
                    setErrorSection((prev) => {
                        return {...prev, ["salesAreas"]: !isValid}
                    });
                });
                trigger("cbPaymentInfo").then((isValid) => {
                    setErrorSection((prev) => {
                        return {...prev, ["cbPaymentInfo"]: !isValid}
                    });
                });

                if(getValues("cbDetails.partnerType") == CBPartnerType.ORGANIZATION) {
                    trigger("cbCorporateInfo").then((isValid) => {
                        setErrorSection((prev) => {
                            return {...prev, ["cbCorporateInfo"]: !isValid}
                        });
                    });
                }

            } else {
                setErrorSection({
                    cbDetails: false,
                    cbPersonalOrganizationInfo: false,
                    cbInvoicingAddress: false,
                    salesAreas: false,
                    cbPaymentInfo: false,
                    cbCorporateInfo: false
                });

                /*
                 * CR-DRIVALIA-EPCH (LPP-22837): se la nuova e-mail di contatto sostituisce una
                 * mail usata anche sull'indirizzo di fatturazione, la scelta su cosa aggiornare
                 * spetta all'operatore: si chiede prima di salvare.
                 */
                const values = getValues();
                if (shouldAskEmailScope(
                        values?.cbDetails?.email,
                        (defaultValues as any)?.cbDetails?.email,
                        values?.cbInvoicingAddress?.invoicingAddrEmail)) {
                    setPendingEmailScopeValues(values);
                    return;
                }

                onSubmit(values);
            }
        });
    }

    return (
        <>
            {hasScope(CBAuth.WRITE_CUSTOMER) && isGlobalEditMode &&
                <Stack
                    direction="row"
                    alignItems={"center"}
                    justifyContent={{
                        sm: "flex-end",
                        xs: "flex-start"
                    }}
                    columnGap={1}
                    mb={{xs: 1}}
                >
                    <Button
                        variant="outlined"
                        color="accent"
                        onClick={onReset}
                    >
                        {t("tabs.buttonLabels.reset")}
                    </Button>
                    <Button
                        color="accent"
                        disabled={!isDirty}
                        onClick={handleSubmit}
                    >
                        {t("tabs.buttonLabels.save")}
                    </Button>
                </Stack>
            }
           { hasScope(CBAuth.WRITE_CUSTOMER) && !isGlobalEditMode && <>
                <Stack
                    direction="row"
                    alignItems={"center"}
                    justifyContent={{
                        sm: "flex-end",
                        xs: "flex-start"
                    }}
                    columnGap={1}
                    mb={{xs: 1}}
                >
                    <Button
                        color="accent"
                        onClick={() => {
                            setShowChangeStatusDialog(true);
                        }}
                        disabled={!hasScope(CBAuth.WRITE_CUSTOMER_STATUS)}
                    >
                        {t("tabs.buttonLabels.changeStatus")}
                    </Button>

                    <Button
                        color="accent"
                        onClick={() => {
                            setIsGlobalEditMode(true);
                        }}
                    >
                        {t("tabs.buttonLabels.edit")}
                    </Button>
                </Stack>

                <Dialog
                    open={showChangeStatusDialog}
                    fullWidth={true}
                    maxWidth={'sm'}
                    onClose={handleCloseChangeStatusDialog}
                >
                    <DialogTitle>{t("customerState.title")}</DialogTitle>
                    <DialogContent>
                        <Box>
                            <FormAutocomplete
                                label={t("status")}
                                name={"status"}
                                control={changeStatusForm.control}
                                fullWidth
                                options={
                                    Object.values(CBCustomerState)
                                        .filter(opt => opt !== customerStateWatch)
                                        .filter(opt => [CBCustomerState.ACTIVE, CBCustomerState.SUSPENDED].includes(opt))
                                }
                                getOptionLabel={opt => toTitleCase(t(`customerState.${opt}`))}
                            />
                        </Box>
                        <Box>
                            <FormAutocomplete
                                label={t("reason")}
                                name={"reason"}
                                control={changeStatusForm.control}
                                fullWidth
                                options={changeStatusReasonQuery.data?.data
                                    .map((opt:any) => opt.code as string)
                                    .filter((code: string) => code !== "ACTIVATION")}
                                getOptionLabel={opt => t(`customerStateChangeReason.${opt}`)}
                                disabled={customerStateWatch !== CBCustomerState.ACTIVE}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button color={"accent"} onClick={handleCloseChangeStatusDialog} variant="outlined">
                            {t("editingModal.buttons.close")}
                        </Button>
                        <Button color={"accent"} onClick={changeStatusForm.handleSubmit(() => setShowConfirmChangeStatus(true))}>
                            {t("editingModal.buttons.confirm")}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={showConfirmChangeStatus}
                    onClose={() => setShowConfirmChangeStatus(false)}
                >
                    <DialogTitle>
                        <WarningAmberRounded color="warning" />
                        {t("message.warning.default")}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            {t("customerStateChangeConfirmation.message")}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowConfirmChangeStatus(false)} autoFocus variant="outlined">
                            {t("customerStateChangeConfirmation.close")}
                        </Button>
                        <Button onClick={() => { setShowConfirmChangeStatus(false); handleSubmitChangesStatusDialog(); }} autoFocus>
                            {t("customerStateChangeConfirmation.confirm")}
                        </Button>
                    </DialogActions>
                </Dialog>
            </>}
            {/* CR-DRIVALIA-EPCH (LPP-22837): scelta dello scope quando la nuova e-mail di contatto
                sostituisce una mail usata anche per la fatturazione. */}
            <CustomerBaseModal
                open={!!pendingEmailScopeValues}
                contentDescription={t("emailScopeModal.description", {
                    email: (defaultValues as any)?.cbDetails?.email || ""
                })}
                buttonConfirmDescription={t("emailScopeModal.buttons.both")}
                buttonAlternativeDescription={t("emailScopeModal.buttons.onlyContact")}
                buttonCloseDescription={t("emailScopeModal.buttons.cancel")}
                onConfirm={() => {
                    const values = pendingEmailScopeValues;
                    setPendingEmailScopeValues(null);
                    onSubmit(values, true);
                }}
                onAlternative={() => {
                    const values = pendingEmailScopeValues;
                    setPendingEmailScopeValues(null);
                    onSubmit(values, false);
                }}
                onClose={() => setPendingEmailScopeValues(null)}
            />
        </>

    )

}

export default CBMultiSectionActionButtons;